/**
 * QuizActiveScreen - Main Quiz Participation Screen
 * 
 * The primary screen shown during active quiz participation.
 * Combines all UI components into a cohesive quiz experience.
 * 
 * Features:
 * - Question display with options
 * - Timer with urgency indicators
 * - Score and streak display
 * - Joker buttons
 * - Progress bar
 * - Results feedback
 */

import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeToggle } from '../../../context/ThemeContext';

// Import UI Components
import { TimerDisplay, InlineTimer, TimerBar } from '../components/TimerDisplay';
import { ScoreDisplay, ScoreBadge, StreakBadge } from '../components/ScoreDisplay';
import { ProgressBar, QuestionBadge } from '../components/ProgressBar';
import { JokerBar, JokerIndicator } from '../components/JokerBar';
import { QuestionCard } from '../components/QuestionCard';

/**
 * QuizActiveScreen component
 * @param {Object} question - Current question object
 * @param {number} questionIndex - Current question index (0-based)
 * @param {number} totalQuestions - Total number of questions
 * @param {Array} selectedOptions - Selected option indices
 * @param {boolean} submitted - Whether answer was submitted
 * @param {boolean} showCorrectAnswer - Whether to show correct answer
 * @param {boolean} answersLocked - Whether answers are locked
 * @param {number} timeRemaining - Seconds remaining
 * @param {boolean} timerExpired - Whether timer has expired
 * @param {boolean} timerActive - Whether timer is active
 * @param {string} urgencyLevel - Timer urgency level
 * @param {string} timerColorClass - Timer color class
 * @param {number} totalScore - Current total score
 * @param {number} currentStreak - Current answer streak
 * @param {number} lastPointsEarned - Points from last answer
 * @param {boolean} pointsAwarded - Whether points were just awarded
 * @param {Object} availableJokers - Joker availability state
 * @param {Object} jokerLoading - Joker loading states
 * @param {Array} hiddenOptions - Options hidden by 50:50 joker
 * @param {Object} audienceResults - Audience joker results
 * @param {Object} bestPlayerAnswer - Best player joker result
 * @param {Function} onSelectOption - Option selection handler
 * @param {Function} onSubmit - Answer submit handler
 * @param {Function} onUseFiftyFifty - 50:50 joker handler
 * @param {Function} onUseAudience - Audience joker handler
 * @param {Function} onUseBestPlayer - Best player joker handler
 * @param {Function} onUseTimeExtension - Time extension joker handler
 * @param {boolean} enableGamification - Whether gamification is enabled
 * @param {boolean} enableJokers - Whether jokers are enabled
 */
export function QuizActiveScreen({
  // Question props
  question,
  questionIndex = 0,
  totalQuestions = 1,
  selectedOptions = [],
  submitted = false,
  showCorrectAnswer = false,
  answersLocked = false,
  
  // Timer props
  timeRemaining = 0,
  timerExpired = false,
  timerActive = false,
  urgencyLevel = 'normal',
  timerColorClass = 'text-gray-600',
  timerBarColorClass = 'bg-green-500',
  bonusTime = 0,
  isPaused = false,
  hasTimer = false,
  
  // Score props
  totalScore = 0,
  currentStreak = 0,
  lastPointsEarned = null,
  pointsAwarded = false,
  
  // Joker props
  availableJokers = {},
  jokerLoading = {},
  hiddenOptions = [],
  audienceResults = null,
  bestPlayerAnswer = null,
  
  // Handlers
  onSelectOption,
  onSubmit,
  onUseFiftyFifty,
  onUseAudience,
  onUseBestPlayer,
  onUseTimeExtension,
  
  // Settings
  enableGamification = false,
  enableJokers = false,
  multiAnswerMode = 'auto',
  shortCode,
}) {
  const { darkMode } = useTheme();

  // Get best player choices array
  const bestPlayerChoices = bestPlayerAnswer?.answer || [];

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header
        className={`
          py-3 px-4 md:px-6
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
          border-b shadow-sm
        `}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          {/* Left: Progress & Code */}
          <div className="flex items-center gap-4">
            <QuestionBadge current={questionIndex + 1} total={totalQuestions} />
            {shortCode && (
              <span className={`hidden sm:inline text-sm font-mono ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                #{shortCode}
              </span>
            )}
          </div>

          {/* Center: Timer */}
          {(timerActive || isPaused || hasTimer) && timeRemaining !== null && (
            <div className="flex-shrink-0 flex items-center gap-1">
              {isPaused && <span className="text-yellow-500">⏸</span>}
              <InlineTimer
                timeRemaining={timeRemaining}
                timerExpired={timerExpired}
                colorClass={isPaused ? 'text-yellow-500' : timerColorClass}
                urgencyLevel={urgencyLevel}
              />
            </div>
          )}

          {/* Right: Score & Theme */}
          <div className="flex items-center gap-3">
            {enableGamification && (
              <>
                <ScoreBadge score={totalScore} />
                <StreakBadge streak={currentStreak} />
              </>
            )}
            {enableJokers && (
              <JokerIndicator availableJokers={availableJokers} />
            )}
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Timer Bar */}
      {(timerActive || isPaused || hasTimer) && timeRemaining !== null && (
        <div className="px-4 py-2">
          <div className="max-w-4xl mx-auto">
            <TimerBar
              timeRemaining={timeRemaining}
              totalTime={30} // This should come from settings
              timerExpired={timerExpired}
              colorClass={isPaused ? 'bg-yellow-500' : timerBarColorClass}
              urgencyLevel={urgencyLevel}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 py-6 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Points Popup (absolute positioned) */}
          {pointsAwarded && lastPointsEarned !== null && lastPointsEarned > 0 && (
            <div className="fixed top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none">
              <div className="text-4xl font-bold text-green-500 animate-bounce">
                +{lastPointsEarned}
              </div>
            </div>
          )}

          {/* Question Card */}
          <QuestionCard
            question={question}
            questionIndex={questionIndex}
            totalQuestions={totalQuestions}
            selectedOptions={selectedOptions}
            submitted={submitted}
            showCorrectAnswer={showCorrectAnswer}
            answersLocked={answersLocked}
            hiddenOptions={hiddenOptions}
            audienceResults={audienceResults}
            bestPlayerChoices={bestPlayerChoices}
            onSelect={onSelectOption}
            onSubmit={onSubmit}
            showSubmitButton={!timerExpired && !showCorrectAnswer}
            multiAnswerMode={multiAnswerMode}
          />

          {/* Best Player Joker Info - Waiting State */}
          {bestPlayerAnswer && bestPlayerAnswer.waiting && (
            <div
              className={`
                mt-4 p-4 rounded-xl
                ${darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}
                border animate-pulse
              `}
            >
              {bestPlayerAnswer.isSelfBestPlayer && (
                <div className={`mb-2 flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-xs font-medium">Du bist der beste Spieler! Zeige Zweitbesten...</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  Warte auf die Antwort...
                </span>
              </div>
            </div>
          )}

          {/* Best Player Joker Info - Answer Revealed */}
          {bestPlayerAnswer && !bestPlayerAnswer.noData && !bestPlayerAnswer.waiting && bestPlayerAnswer.answer && (
            <div
              className={`
                mt-4 p-4 rounded-xl
                ${darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}
                border
              `}
            >
              {bestPlayerAnswer.isSelfBestPlayer && (
                <div className={`mb-2 flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-xs font-medium">Du bist der beste Spieler! Hier die Antwort des Zweitbesten:</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  Diese Antwort liegt vorne mit {bestPlayerAnswer.playerScore} Pkt.
                </span>
              </div>
            </div>
          )}

          {/* Audience Joker Info */}
          {audienceResults && (
            <div
              className={`
                mt-4 p-4 rounded-xl
                ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}
                border
              `}
            >
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className={`font-medium ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                  Publikums-Abstimmung
                </span>
              </div>
            </div>
          )}

          {/* Joker Bar */}
          {enableJokers && !showCorrectAnswer && !answersLocked && (
            <div className="mt-6">
              <JokerBar
                availableJokers={{
                  ...availableJokers,
                  // Time extension joker is disabled - admin controls time now
                  timeExtension: false
                }}
                jokerLoading={jokerLoading}
                onUseFiftyFifty={onUseFiftyFifty}
                onUseAudience={onUseAudience}
                onUseBestPlayer={onUseBestPlayer}
                onUseTimeExtension={null}
                disabled={submitted || timerExpired}
                layout="horizontal"
                showLabels={true}
              />
            </div>
          )}

          {/* Score Display (below question) */}
          {enableGamification && (
            <div className="mt-6 flex justify-center">
              <ScoreDisplay
                totalScore={totalScore}
                currentStreak={currentStreak}
                lastPointsEarned={lastPointsEarned}
                pointsAwarded={pointsAwarded}
              />
            </div>
          )}
        </div>
      </main>

      {/* Footer Progress */}
      <footer
        className={`
          py-4 px-4
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
          border-t
        `}
      >
        <div className="max-w-4xl mx-auto">
          <ProgressBar
            currentIndex={questionIndex}
            total={totalQuestions}
            showPercentage
            variant="bar"
            size="sm"
          />
        </div>
      </footer>
    </div>
  );
}

export default QuizActiveScreen;
